import React, { useState, useEffect } from "react";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  ArrowUp,
  ArrowDown,
  Activity,
  Users,
  Award,
  DollarSign,
  Calendar,
  TrendingUp,
  Loader,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import ReferralService from "../../../services/api/admin.referral.service";
import { FaRupeeSign } from "react-icons/fa";

const ReferralStats = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState("month");
  const [viewMode, setViewMode] = useState("table");
  const [apiResponse, setApiResponse] = useState(null);

  // Fetch referral stats
  const fetchReferralStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await ReferralService.getReferralStats(timeRange);
    
      if (response.success) {
        setStats(response.data);
        setApiResponse(response);
      } else {
        throw new Error(response.error);
      }
    } catch (err) {
      console.error("Error fetching referral stats:", err);
      setError(err.message || "Failed to load referral statistics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferralStats();
  }, [timeRange]);

  // Format large numbers with commas
  const formatNumber = (num) => {
    if (num === undefined || num === null) return "0";
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Format percentage change
  const formatPercentageChange = (value) => {
    if (value === undefined || value === null)
      return { value: "N/A", positive: null };
    const isPositive = value >= 0;
    return {
      value: `${Math.abs(value).toFixed(2)}%`,
      positive: isPositive,
    };
  };

  // Calculate totals from available data
  const calculateTotals = () => {
    if (!stats)
      return { totalReferrals: 0, totalRewards: 0, activeReferrals: 0 };

    // Get total referrals from totals object (this is the correct source)
    const totalReferrals = stats.totals?.totalReferrals || 0;

    // Get total rewards from totals object (this is the correct source)
    const totalRewards = stats.totals?.totalRewards || 0;

    // Get active referrals count
    const activeReferrals = stats.statusCounts?.ACTIVE || 0;

    return { totalReferrals, totalRewards, activeReferrals };
  };

  // Process top referrers data to include proper counts
  const processTopReferrers = () => {
    if (!stats.topReferrers) return [];

    // Process top referrers with the data we have
    const processedReferrers = stats.topReferrers
      .slice(0, 10)
      .map((referrer, index) => {
        return {
          ...referrer,
          // Use the totalReferrals and totalRewards from the API response
          totalReferrals: referrer.totalReferrals || 0,
          totalRewards: referrer.totalRewards || 0,
          fullName:
            referrer.fullName ||
            `User ${(referrer._id || "").slice(-6) || "Unknown"}`,
          email: referrer.email || "N/A",
        };
      });

    // Sort by actual referral count
    return processedReferrers.sort(
      (a, b) => b.totalReferrals - a.totalReferrals
    );
  };

  // Generate summary cards data
  const getSummaryCards = () => {
    const totals = calculateTotals();
    const conversionRate = stats?.conversionStats?.overallRate || 0;

    return [
      {
        title: "Total Referrals",
        value: formatNumber(totals.totalReferrals),
        change: formatPercentageChange(0),
        icon: <Users className="w-6 h-6 text-blue-500" />,
        color: "bg-blue-50",
      },
      {
        title: "Active Referrals",
        value: formatNumber(totals.activeReferrals),
        change: formatPercentageChange(0),
        icon: <Activity className="w-6 h-6 text-green-500" />,
        color: "bg-green-50",
      },
      {
        title: "Total Rewards",
        value: `₹${formatNumber(totals.totalRewards)}`,
        change: formatPercentageChange(0),
        icon: <FaRupeeSign className="w-6 h-6 text-yellow-500" />,
        color: "bg-yellow-50",
      },
      {
        title: "Conversion Rate",
        value: `${conversionRate}%`,
        change: formatPercentageChange(0),
        icon: <TrendingUp className="w-6 h-6 text-purple-500" />,
        color: "bg-purple-50",
      },
    ];
  };

  // Format status with color
  const formatStatus = (status) => {
    if (!status) return { text: "N/A", color: "text-gray-500" };

    const statusMap = {
      ACTIVE: {
        text: "Active",
        color:
          "text-green-600 bg-green-100 px-2 py-1 rounded-full text-xs font-medium",
      },
      QUALIFIED: {
        text: "Qualified",
        color:
          "text-blue-600 bg-blue-100 px-2 py-1 rounded-full text-xs font-medium",
      },
      CONVERTED: {
        text: "Converted",
        color:
          "text-purple-600 bg-purple-100 px-2 py-1 rounded-full text-xs font-medium",
      },
      PENDING: {
        text: "Pending",
        color:
          "text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full text-xs font-medium",
      },
    };

    return (
      statusMap[status] || {
        text: status,
        color:
          "text-gray-600 bg-gray-100 px-2 py-1 rounded-full text-xs font-medium",
      }
    );
  };

  // Format verification status
  const formatVerificationStatus = (status) => {
    if (!status) return { text: "N/A", color: "text-gray-500" };

    const statusMap = {
      verified: {
        text: "Verified",
        color:
          "text-green-600 bg-green-100 px-2 py-1 rounded-full text-xs font-medium",
      },
      pending: {
        text: "Pending",
        color:
          "text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full text-xs font-medium",
      },
      rejected: {
        text: "Rejected",
        color:
          "text-red-600 bg-red-100 px-2 py-1 rounded-full text-xs font-medium",
      },
    };

    return (
      statusMap[status] || {
        text: status,
        color:
          "text-gray-600 bg-gray-100 px-2 py-1 rounded-full text-xs font-medium",
      }
    );
  };

  // Empty state component
  const EmptyState = ({ message }) => (
    <div className="flex flex-col items-center justify-center py-10 bg-gray-50 rounded-lg border border-gray-200">
      <AlertCircle className="w-12 h-12 text-gray-400 mb-3" />
      <p className="text-gray-500 text-center">{message}</p>
      <button
        onClick={fetchReferralStats}
        className="mt-4 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-lg transition"
      >
        <RefreshCw className="w-4 h-4 mr-2 inline" />
        Refresh Data
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center py-12">
          <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 p-4 rounded-md text-red-700">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <h3 className="font-medium">Error fetching data</h3>
          </div>
          <p className="mt-2 text-sm">{error}</p>
          <button
            className="mt-3 text-sm font-medium text-red-600 hover:text-red-800 underline"
            onClick={fetchReferralStats}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="p-6">
        <EmptyState message="No referral statistics available. Please try changing your filters or try again later." />
      </div>
    );
  }

  return (
    <div className="p-3 md:p-6 w-full max-w-full overflow-hidden">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 pb-4 border-b border-gray-200">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800 mb-1">
            Referral Statistics
          </h1>
          <p className="text-sm text-gray-500">
            Track and analyze your referral program performance
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mt-4 md:mt-0 w-full md:w-auto">
          {/* View mode selector */}
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              onClick={() => setViewMode("table")}
              className={`px-4 py-2 text-sm font-medium rounded-l-lg ${
                viewMode === "table"
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
              }`}
            >
              Table
            </button>
            <button
              type="button"
              onClick={() => setViewMode("chart")}
              className={`px-4 py-2 text-sm font-medium rounded-r-lg ${
                viewMode === "chart"
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
              }`}
            >
              Chart
            </button>
          </div>

          {/* Refresh button */}
          <button
            onClick={fetchReferralStats}
            className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
            disabled={loading}
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
            />
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {getSummaryCards().map((card, index) => (
          <div
            key={index}
            className={`${card.color} p-5 rounded-lg shadow-sm border border-gray-100`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">
                  {card.title}
                </p>
                <p className="text-2xl font-bold mt-2 text-gray-800">
                  {card.value}
                </p>
                <div className="flex items-center mt-3">
                  {card.change.positive !== null &&
                    (card.change.positive ? (
                      <ArrowUp className="w-4 h-4 text-green-500 mr-1" />
                    ) : (
                      <ArrowDown className="w-4 h-4 text-red-500 mr-1" />
                    ))}
                  <span
                    className={`text-sm ${
                      card.change.positive === null
                        ? "text-gray-500"
                        : card.change.positive
                        ? "text-green-500"
                        : "text-red-500"
                    }`}
                  >
                    {card.change.value}
                  </span>
                </div>
              </div>
              <div className="p-3 rounded-full bg-white shadow-sm">
                {card.icon}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Reward Stats */}
      {stats.rewardStats?.currentConfig && (
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200 mb-8">
          <h2 className="text-lg font-medium text-gray-800 mb-1">
            Current Reward Configuration
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            Active referral program reward details
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-amber-50 p-4 rounded-lg">
              <p className="text-sm text-gray-500 font-medium">
                Reward Amount Per Referral
              </p>
              <p className="text-xl font-bold text-gray-800 mt-1">
                ₹{formatNumber(stats.rewardStats.currentConfig.amount || 0)}
              </p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <p className="text-sm text-gray-500 font-medium">Description</p>
              <p className="text-base font-medium text-gray-800 mt-1">
                {stats.rewardStats.currentConfig.description || "N/A"}
              </p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-gray-500 font-medium">
                Total Rewards Distributed
              </p>
              <p className="text-xl font-bold text-gray-800 mt-1">
                ₹{formatNumber(stats.totals?.totalRewards || 0)}
              </p>
            </div>
          </div>
        </div>
      )}

      {viewMode === "chart" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Referral Growth Chart */}
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-800 mb-1">
              Referral Growth
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Trend of referrals over time
            </p>
            {stats.recentActivity && stats.recentActivity.length > 0 ? (
              <div className="h-64 md:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={stats.recentActivity}
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="_id"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        });
                      }}
                    />
                    <YAxis />
                    <Tooltip
                      formatter={(value, name) => [
                        formatNumber(value),
                        "Referrals",
                      ]}
                      labelFormatter={(value) => {
                        const date = new Date(value);
                        return date.toLocaleDateString("en-US", {
                          weekday: "long",
                          month: "long",
                          day: "numeric",
                        });
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="count"
                      name="Referrals"
                      stroke="#3B82F6"
                      activeDot={{ r: 8 }}
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState message="No time series data available for the selected period" />
            )}
          </div>

          {/* KYC Status Distribution Chart */}
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-medium text-gray-800 mb-1">
              KYC Status Distribution
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Breakdown of KYC verification statuses
            </p>
            {stats.kycStats?.statusDistribution ? (
              <div className="h-64 md:h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={Object.entries(stats.kycStats.statusDistribution).map(
                      ([status, count]) => ({
                        status:
                          status.charAt(0).toUpperCase() + status.slice(1),
                        count,
                      })
                    )}
                    margin={{ top: 5, right: 20, left: 10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="status" tick={{ fontSize: 12 }} />
                    <YAxis />
                    <Tooltip
                      formatter={(value) => [formatNumber(value), "Count"]}
                    />
                    <Legend />
                    <Bar
                      dataKey="count"
                      name="Count"
                      fill="#10B981"
                      radius={[4, 4, 0, 0]}
                      barSize={60}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState message="No KYC status distribution data available" />
            )}
          </div>
        </div>
      )}

      {/* Top Referrers Table */}
      <div className="mb-8">
        <h3 className="text-lg font-medium mb-4">Top Referrers</h3>
        {stats.topReferrers && stats.topReferrers.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Referral Code
                  </th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Referrals
                  </th>
                  <th className="px-3 md:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Rewards
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {processTopReferrers().map((referrer, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 md:px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      #{index + 1}
                    </td>
                    <td className="px-3 md:px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">{referrer.fullName}</div>
                        <div className="text-gray-500 text-xs">
                          {referrer.email}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 md:px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {referrer.referralCode}
                    </td>
                    <td className="px-3 md:px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatNumber(referrer.totalReferrals)}
                    </td>
                    <td className="px-3 md:px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      ₹{formatNumber(referrer.totalRewards)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 p-4 rounded-lg text-center text-gray-500">
            No top referrers data available.
          </div>
        )}
      </div>

      {/* Tracked Referrals Table */}
      <div>
        <h3 className="text-lg font-medium mb-4">Referral Tracking</h3>
        {stats.trackedReferrals && stats.trackedReferrals.length > 0 ? (
          <div className="overflow-x-auto rounded-lg border">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 sm:px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Referral Code
                  </th>
                  <th className="px-2 sm:px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Referrer
                  </th>
                  <th className="px-2 sm:px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Referee
                  </th>
                  <th className="px-2 sm:px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-2 sm:px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    KYC Status
                  </th>
                  <th className="px-2 sm:px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stats.trackedReferrals.map((referral, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {referral.referralCode}
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">
                          {referral.referrer?.fullName || "N/A"}
                        </div>
                        <div className="text-gray-500 text-xs">
                          {referral.referrer?.email || "N/A"}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div className="font-medium">
                          {referral.referee?.fullName || "N/A"}
                        </div>
                        <div className="text-gray-500 text-xs">
                          {referral.referee?.email || "N/A"}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm">
                      <span className={formatStatus(referral.status).color}>
                        {formatStatus(referral.status).text}
                      </span>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm">
                      <span
                        className={
                          formatVerificationStatus(
                            referral.refereeKyc?.verificationStatus
                          ).color
                        }
                      >
                        {
                          formatVerificationStatus(
                            referral.refereeKyc?.verificationStatus
                          ).text
                        }
                      </span>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(referral.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-gray-50 p-4 rounded-lg text-center text-gray-500">
            No tracked referrals data available.
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralStats;
